import React, { useMemo, useState } from 'react';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Tag from '@amzn/meridian/tag';
import Tab, { TabGroup } from '@amzn/meridian/tab';
import ButtonGroup, { ButtonOption } from '@amzn/meridian/button-group';
import Table, { TableRow, TableCell } from '@amzn/meridian/table';
import { CompareFlowMonitoringDataOutput, MetricComparisonData, MonitoringEntityIdentityComparisonData } from '../model';
import { MetricName, ProcessPathType } from '../constants';
import { formatWholeMetricValue, formatDeltaPercent, getStatusTagType } from '../utils';

/** Metric tabs shown above the table — one metric visible at a time. */
const METRIC_TABS: { value: MetricName; label: string }[] = [
    { value: MetricName.VOLUME_PROCESSED, label: 'Volume' },
    { value: MetricName.UPH, label: 'Rates' },
    { value: MetricName.HEADCOUNT, label: 'HC' },
];

/** Primary/Support path filter. */
type PathFilter = 'all' | 'primary' | 'support';

/**
 * Support/indirect iff the authoritative `pathType` is INDIRECT; falls back to the
 * process-path name containing "support" while the service rolls `pathType` out.
 */
const isSupportEntity = (entity: MonitoringEntityIdentityComparisonData): boolean => {
    const pathType = entity.monitoringEntityIdentity.processPath?.pathType;
    if (pathType) return pathType === ProcessPathType.INDIRECT;
    return !!entity.monitoringEntityIdentity.processPath?.processName
        ?.toLowerCase()
        .includes('support');
};

interface ProgressReportTableProps {
    compareResponse: CompareFlowMonitoringDataOutput;
}

const getDisplayName = (entity: MonitoringEntityIdentityComparisonData): string =>
    entity.monitoringEntityIdentity.processPath?.displayName ||
    entity.monitoringEntityIdentity.processPath?.functionName ||
    '';

const findMetric = (
    entity: MonitoringEntityIdentityComparisonData | undefined,
    metric: MetricName
): MetricComparisonData | undefined =>
    entity?.metricsComparisonList.find((m) => m.metricName === metric);

/** A single actual value with its delta-percentage badge stacked underneath. */
const MetricCell: React.FC<{ metric?: MetricComparisonData }> = ({ metric }) => {
    if (!metric) {
        return <Text type="b300" color="secondary">—</Text>;
    }
    const deltaLabel = formatDeltaPercent(metric.variancePercentage);
    return (
        <Column spacing="100" alignmentHorizontal="end">
            <Text type="b400">{formatWholeMetricValue(metric.actualValue)}</Text>
            {deltaLabel && (
                <Tag type={getStatusTagType(metric.comparisonStatus)}>{deltaLabel}</Tag>
            )}
        </Column>
    );
};

/**
 * Progress Report table (design refresh). Renders the shift-progress comparison
 * as a grouped, expandable table showing ONE metric at a time (Volume / Rates /
 * HC), chosen via tabs. Each process path is a parent row (actual value + delta%
 * badge across a Total column and one column per hour window); its nested "Plan"
 * row shows the expected (planned) values for the same columns.
 *
 * Uses Meridian's native Table nested rows (`<TableRow open>`) — same pattern as
 * the V2 ShiftDataTable — so expand/collapse needs no custom caret or CSS.
 */
const ProgressReportTable: React.FC<ProgressReportTableProps> = ({ compareResponse }) => {
    const [selectedMetric, setSelectedMetric] = useState<MetricName>(MetricName.VOLUME_PROCESSED);
    const [pathFilter, setPathFilter] = useState<PathFilter>('all');
    // Which process-path rows are collapsed (default: all expanded, Plan visible).
    const [collapsedRows, setCollapsedRows] = useState<Set<string>>(new Set());

    const toggleRow = (key: string) => {
        setCollapsedRows((prev) => {
            const next = new Set(prev);
            if (next.has(key)) {
                next.delete(key);
            } else {
                next.add(key);
            }
            return next;
        });
    };

    const windows = compareResponse.hourlyShiftComparisonDataList ?? [];

    // Per process path: the Total metric plus one metric per hour window, resolved
    // for the currently selected metric tab. Ordered DIRECT paths first, then
    // INDIRECT; within each group, highest Total (selected metric) first.
    const rows = useMemo(() => {
        const entities = compareResponse.shiftTotalsComparison.filter((entity) => {
            if (pathFilter === 'primary') return !isSupportEntity(entity);
            if (pathFilter === 'support') return isSupportEntity(entity);
            return true;
        });
        const sorted = [...entities].sort((a, b) => {
            // DIRECT (0) before INDIRECT (1).
            const groupDelta = (isSupportEntity(a) ? 1 : 0) - (isSupportEntity(b) ? 1 : 0);
            if (groupDelta !== 0) return groupDelta;
            // Highest Total first; missing totals sink to the bottom of the group.
            const aTotal = findMetric(a, selectedMetric)?.actualValue ?? -Infinity;
            const bTotal = findMetric(b, selectedMetric)?.actualValue ?? -Infinity;
            return bTotal - aTotal;
        });
        return sorted.map((entity) => {
            const displayName = getDisplayName(entity);
            const total = findMetric(entity, selectedMetric);
            const hourly = windows.map((window) => {
                const windowEntity = window.monitoringEntityIdentitiesComparisonDataList.find(
                    (e) => getDisplayName(e) === displayName
                );
                return findMetric(windowEntity, selectedMetric);
            });
            return { displayName, total, hourly };
        });
    }, [compareResponse, selectedMetric, windows, pathFilter]);

    return (
        <Column spacing="400">
                <ButtonGroup
                    value={pathFilter}
                    onChange={(value: string) => setPathFilter(value as PathFilter)}
                    size="small"
                >
                    <ButtonOption value="all">All Paths</ButtonOption>
                    <ButtonOption value="primary">Primary Paths</ButtonOption>
                    <ButtonOption value="support">Support Paths</ButtonOption>
                </ButtonGroup>

                <TabGroup
                    fill="none"
                    value={selectedMetric}
                    onChange={(value: string) => setSelectedMetric(value as MetricName)}
                    aria-labelledby="progress-report-metric-tabs"
                >
                    {METRIC_TABS.map((tab) => (
                        <Tab key={tab.value} value={tab.value} aria-controls={tab.value} id={tab.value}>
                            {tab.label}
                        </Tab>
                    ))}
                </TabGroup>

            <Table
                headerRows={1}
                headerColumns={1}
                showDividers={true}
                fixHeaderRows={false}
                spacing="small"
            >
                {/* Header: sticky "Path" + Total + one column per hour window */}
                <TableRow>
                    <TableCell header={true}>Path</TableCell>
                    <TableCell header={true} alignmentHorizontal="end">Total</TableCell>
                    {windows.map((window) => (
                        <TableCell key={window.startTime} header={true} alignmentHorizontal="end">
                            {window.startTime}
                        </TableCell>
                    ))}
                </TableRow>

                {rows.map((row) => {
                    const isOpen = !collapsedRows.has(row.displayName);
                    return (
                        // Parent = actual values (+ delta badge); nested child = the Plan row.
                        <TableRow
                            key={row.displayName}
                            open={isOpen}
                            onClick={() => toggleRow(row.displayName)}
                            highlightOnHover={true}
                        >
                            <TableCell header={true}>{row.displayName}</TableCell>
                            <TableCell alignmentHorizontal="end">
                                <MetricCell metric={row.total} />
                            </TableCell>
                            {row.hourly.map((metric, i) => (
                                <TableCell key={windows[i].startTime} alignmentHorizontal="end">
                                    <MetricCell metric={metric} />
                                </TableCell>
                            ))}

                            <TableRow>
                                <TableCell header={true}>
                                    <Text type="b200" color="secondary">Plan</Text>
                                </TableCell>
                                <TableCell alignmentHorizontal="end">
                                    <Text type="b300" color="secondary">
                                        {row.total ? formatWholeMetricValue(row.total.expectedValue) : '—'}
                                    </Text>
                                </TableCell>
                                {row.hourly.map((metric, i) => (
                                    <TableCell key={windows[i].startTime} alignmentHorizontal="end">
                                        <Text type="b300" color="secondary">
                                            {metric ? formatWholeMetricValue(metric.expectedValue) : '—'}
                                        </Text>
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableRow>
                    );
                })}
            </Table>
        </Column>
    );
};

export default ProgressReportTable;
